/*
 * File: StudentActionsMenu.tsx
 * Author: Austin Robinson
 * Date Created: 12/07/2025
 * Copyright (c) DrivingDoc 2025
 */
import BadgeIcon from '@mui/icons-material/Badge';
import CancelIcon from '@mui/icons-material/Cancel';
import MoneyOffIcon from '@mui/icons-material/MoneyOff';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useDialogs } from '@toolpad/core/useDialogs';

import {
  cancelPeriodEnrollmentMutation,
  countSelfSchedulePeriodsQueryKey,
  deletePeriodEnrollmentMutation,
  getCoursePeriodQueryKey,
  getStudentLedgerQueryKey,
  getStudentScheduleQueryKey,
  listSelfSchedulePeriodsQueryKey,
  listStudentCourseEnrollmentsQueryKey,
} from '@drivingdoc/api-client/generated/react-query';

import FeedbackDialog from 'components/Dialogs/FeedbackDialog';
import UpdateStudentPermitDialog from 'components/Dialogs/Students/UpdateStudentPermitDialog';
import RequireRole from 'components/RequireRole';

import { useMenuControl } from 'hooks/useMenuControl';

import { getApiErrorMessage } from 'utils/getApiErrorMessage';

import { useDriveOverviewDialogContext } from '../../context';
import { CountSelfSchedulePeriodsData, ListSelfSchedulePeriodsData, Options } from '@drivingdoc/api-client/generated';

interface StudentActionsMenuProps {
  studentId: string;
  coursePeriodId: string;
  studentName: string;
}

export function StudentActionsMenu({ studentId, coursePeriodId, studentName }: StudentActionsMenuProps) {
  const studentActionsMenu = useMenuControl();
  const dialogs = useDialogs();
  const queryClient = useQueryClient();
  const { onClose } = useDriveOverviewDialogContext();

  const invalidateEnrollmentQueries = () => {
    queryClient.invalidateQueries({
      queryKey: getCoursePeriodQueryKey({ path: { id: coursePeriodId } }),
    });
    queryClient.invalidateQueries({
      queryKey: listStudentCourseEnrollmentsQueryKey({ path: { id: studentId } }),
      exact: false,
    });
    queryClient.invalidateQueries({
      queryKey: getStudentLedgerQueryKey({ path: { id: studentId } }),
      exact: false,
    });
    queryClient.invalidateQueries({
      queryKey: getStudentScheduleQueryKey({ path: { id: studentId } }),
      exact: false,
    });
    queryClient.invalidateQueries({
      queryKey: countSelfSchedulePeriodsQueryKey({} as Options<CountSelfSchedulePeriodsData>),
      exact: false,
    });
    queryClient.invalidateQueries({
      queryKey: listSelfSchedulePeriodsQueryKey({} as Options<ListSelfSchedulePeriodsData>),
      exact: false,
    });
  };

  const deleteEnrollmentMutation = useMutation({
    ...deletePeriodEnrollmentMutation(),
    onSuccess: invalidateEnrollmentQueries,
  });

  const cancelEnrollmentMutation = useMutation({
    ...cancelPeriodEnrollmentMutation(),
    onSuccess: invalidateEnrollmentQueries,
  });

  const handleRemoveStudent = async () => {
    studentActionsMenu.closeMenu();

    const confirmed = await dialogs.confirm(`Are you sure you want to remove ${studentName} from this drive?`, {
      okText: 'Remove',
      cancelText: 'Cancel',
      severity: 'error',
    });

    if (!confirmed) {
      return;
    }

    try {
      await deleteEnrollmentMutation.mutateAsync({ body: { coursePeriodId, studentId } });
      dialogs.open(FeedbackDialog, {
        icon: 'success',
        title: 'Student removed from drive',
      });
      onClose();
    } catch (err) {
      dialogs.open(FeedbackDialog, {
        title: 'Failed to remove student from drive',
        message: getApiErrorMessage(err),
      });
    }
  };

  const handleCancelDrive = async () => {
    studentActionsMenu.closeMenu();

    const confirmed = await dialogs.confirm(
      `Are you sure you want to cancel the drive for ${studentName}? Late fees may be applied.`,
      {
        okText: 'Cancel Drive',
        cancelText: 'Go Back',
        severity: 'error',
      },
    );

    if (!confirmed) {
      return;
    }

    try {
      await cancelEnrollmentMutation.mutateAsync({ body: { coursePeriodId, studentId, cancel: true } });
      dialogs.open(FeedbackDialog, {
        icon: 'success',
        title: 'Drive canceled',
      });
      onClose();
    } catch (err) {
      dialogs.open(FeedbackDialog, {
        title: 'Failed to cancel drive',
        message: getApiErrorMessage(err),
      });
    }
  };

  const handleCancelWithoutFees = async () => {
    studentActionsMenu.closeMenu();

    const confirmed = await dialogs.confirm(
      `Are you sure you want to cancel the drive for ${studentName} without late fees?`,
      {
        okText: 'Cancel Without Fees',
        cancelText: 'Go Back',
      },
    );

    if (!confirmed) {
      return;
    }

    try {
      await cancelEnrollmentMutation.mutateAsync({
        body: {
          coursePeriodId,
          studentId,
          cancel: true,
          overrideLateFee: true,
        },
      });
      dialogs.open(FeedbackDialog, {
        icon: 'success',
        title: 'Drive canceled without late fees',
      });
      onClose();
    } catch (err) {
      dialogs.open(FeedbackDialog, {
        title: 'Failed to cancel drive',
        message: getApiErrorMessage(err),
      });
    }
  };

  const handleUpdatePermit = () => {
    studentActionsMenu.closeMenu();
    dialogs.open(UpdateStudentPermitDialog, { id: studentId });
  };

  const isLoading = deleteEnrollmentMutation.isPending || cancelEnrollmentMutation.isPending;

  return (
    <>
      <Tooltip title="Student Actions">
        <IconButton onClick={studentActionsMenu.openMenu} size="small">
          <MoreVertIcon />
        </IconButton>
      </Tooltip>

      <Menu
        {...studentActionsMenu.menuProps}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <RequireRole role={['ADMIN', 'INSTRUCTOR']}>
          <MenuItem onClick={handleUpdatePermit} disabled={isLoading}>
            <ListItemIcon>
              <BadgeIcon />
            </ListItemIcon>
            Update Permit Information
          </MenuItem>
        </RequireRole>

        <RequireRole role="ADMIN">
          <Divider />

          <MenuItem onClick={handleRemoveStudent} disabled={isLoading}>
            <ListItemIcon>
              <PersonRemoveIcon sx={{ color: 'error.main' }} />
            </ListItemIcon>
            Remove Student from Drive
          </MenuItem>

          <MenuItem onClick={handleCancelDrive} disabled={isLoading}>
            <ListItemIcon>
              <CancelIcon sx={{ color: 'error.main' }} />
            </ListItemIcon>
            Cancel Drive
          </MenuItem>

          <MenuItem onClick={handleCancelWithoutFees} disabled={isLoading}>
            <ListItemIcon>
              <MoneyOffIcon />
            </ListItemIcon>
            Cancel Drive (No Late Fees)
          </MenuItem>
        </RequireRole>
      </Menu>

      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={isLoading}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </>
  );
}

export default StudentActionsMenu;
